import { QUESTION_TYPES } from '../constants'

/**
 * Creates a unique local ID used as the `key` in v-for.
 * This is not a backend ID, so it is never sent to the API.
 * Example: generateUniqueId('section') -> "section-1728370000000-a1b2c3"
 */
export function generateUniqueId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/**
 * Normalizes text for name comparison:
 * all whitespace is removed and the text is lowercased, so
 * "Is the tire damaged?" and "isthetiredamaged?" are treated as equal.
 */
const normalize = (value) => value?.replace(/\s+/g, '').toLowerCase()

/**
 * Checks whether the item at `itemIndex` has the same name as any other
 * item in `items` (after normalization).
 * Empty names are not treated as duplicates (handled by the "required" validation).
 */
export function hasDuplicateName(items, itemIndex) {
  const name = normalize(items[itemIndex]?.name)
  return !!name && items.some((item, index) => index !== itemIndex && normalize(item.name) === name)
}

export function isInspectionQuestionInvalid(questions, questionIndex) {
  const question = questions[questionIndex]
  return !question.name || !question.type || hasDuplicateName(questions, questionIndex)
}

export function isInspectionSectionInvalid(sections, sectionIndex) {
  const section = sections[sectionIndex]
  return (
    !section.name ||
    hasDuplicateName(sections, sectionIndex) ||
    section.questions.some((_, questionIndex) =>
      isInspectionQuestionInvalid(section.questions, questionIndex)
    )
  )
}

export function hasInvalidInspectionSections(sections) {
  return sections.some((_, sectionIndex) => isInspectionSectionInvalid(sections, sectionIndex))
}

/**
 * Converts the builder's form state into the API payload.
 * - `sortOrder` is derived from the index, so drag-and-drop order is saved automatically.
 * - `sectionId` / `questionId` are sent only for existing items (the backend updates them).
 * - Items missing from the payload are treated as deleted by the backend.
 * - Note/photo policies are only sent when their toggle is enabled.
 * - Healthy/unhealthy labels only apply to the options type (radio-button).
 */
export function buildSectionsPayload(sections) {
  return sections.map((section, sectionIndex) => ({
    ...(section.sectionId != null && { sectionId: section.sectionId }),
    name: section.name,
    sortOrder: sectionIndex + 1,
    questions: section.questions.map((question, questionIndex) => {
      const isOptions = question.type === QUESTION_TYPES.options

      return {
        ...(question.questionId != null && { questionId: question.questionId }),
        question: question.name,
        sortOrder: questionIndex + 1,
        type: isOptions ? QUESTION_TYPES.options : QUESTION_TYPES.text,
        isRequired: question.isRequired,
        notePolicy: question.isNoteEnabled ? question.notePolicy : null,
        photoPolicy: question.isPhotoEnabled ? question.photoPolicy : null,
        unhealthyLabel: isOptions
          ? question.options.find((option) => option.value === false)?.label ?? null
          : null,
        healthyLabel: isOptions
          ? question.options.find((option) => option.value === true)?.label ?? null
          : null,
      }
    }),
  }))
}

/**
 * Converts the API response into the builder's state shape
 * (the inverse of buildSectionsPayload).
 * @param {Array} sections - sections from the API
 * @param {boolean} isDuplicate - true when the data is used as a new template:
 *   backend IDs are set to null (so they are treated as new records) and `key` is random.
 *   false (edit mode): backend IDs are kept and `key` is derived from them.
 *
 * Details:
 * - Sections and questions are sorted by `sortOrder`.
 * - Only the first section is expanded (`isExpanded`).
 * - The note/photo toggles are enabled when the corresponding policy exists.
 * - `options` is always ordered [unhealthy (false), healthy (true)], because the UI
 *   shows the healthy option on the right side.
 */
export function mapSectionsToForm(sections = [], isDuplicate = false) {
  return [...sections]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((section, index) => ({
      key: isDuplicate ? generateUniqueId('section') : `s-${section.sectionId}`,
      sectionId: isDuplicate ? null : section.sectionId,
      name: section.name,
      isExpanded: index === 0,
      questions: [...section.questions]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((question) => ({
          key: isDuplicate ? generateUniqueId('question') : `q-${question.questionId}`,
          questionId: isDuplicate ? null : question.questionId,
          name: question.question,
          type:
            question.type === QUESTION_TYPES.options ? QUESTION_TYPES.options : QUESTION_TYPES.text,
          isRequired: question.isRequired,
          isNoteEnabled: !!question.notePolicy,
          notePolicy: question.notePolicy,
          isPhotoEnabled: !!question.photoPolicy,
          photoPolicy: question.photoPolicy,
          options: [
            { value: false, label: question.unhealthyLabel ?? '' },
            { value: true, label: question.healthyLabel ?? '' },
          ],
        })),
    }))
}

/**
 * Builds the name for a duplicated item by adding a sequence number at the end.
 * - "Inspection"     -> "Inspection (1)"
 * - "Inspection (1)" -> "Inspection (2)"
 * The regex only matches "(number)" at the end of the name, preceded by a space.
 * Existing names can be provided to avoid duplicate suffixes.
 */
export function getDuplicateName(name = '', existingNames = []) {
  const match = name.match(/^(.*)\s\((\d+)\)$/)
  const baseName = match ? match[1] : name
  let number = match ? Number(match[2]) + 1 : 1
  let duplicateName = `${baseName} (${number})`

  while (
    existingNames.some((existingName) => normalize(existingName) === normalize(duplicateName))
  ) {
    number += 1
    duplicateName = `${baseName} (${number})`
  }

  return duplicateName
}

export function hasAccess(store, type) {
  const accessMap = {
    read: 'c19a-default-vehicle-inspection:read',
    update: 'c19a-default-vehicle-inspection:update',
    create: 'c19a-default-vehicle-inspection:create',
    delete: 'c19a-default-vehicle-inspection:delete',
  }

  const permission = accessMap[type]

  if (!permission) return false

  return store.getters['identity/hasAccess'](permission)
}
